import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { Library, LayoutDashboard, BookOpen, Users, Settings, LogOut, X } from 'lucide-react';
import { cn } from '../lib/utils';

const SidebarItem = ({ icon: Icon, label, active, onClick }) => {
    return (
        <motion.div
            whileHover={{ scale: 1.02, x: 4 }}
            whileTap={{ scale: 0.98 }}
            onClick={onClick}
            className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-all duration-200 group",
                active
                    ? "bg-primary/20 text-primary shadow-[0_0_20px_rgba(59,130,246,0.3)] border border-primary/20"
                    : "text-slate-400 hover:bg-white/5 hover:text-white"
            )}
        >
            <Icon size={20} className={cn("transition-colors", active ? "text-primary" : "group-hover:text-white")} />
            <span className="font-medium">{label}</span>
            {active && (
                <motion.div
                    layoutId="active-pill"
                    className="absolute left-0 w-1 h-8 bg-primary rounded-r-full"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                />
            )}
        </motion.div>
    );
};

export default function Sidebar({ activeItem, setActiveItem, isOpen, setIsOpen, onLogout }) {
    const currentUser = useSelector((state) => state.user.currentUser);

    const menuItems = [
        { id: 'Dashboard', icon: LayoutDashboard, label: 'Tableau de bord' },
        { id: 'Books', icon: BookOpen, label: 'Livres' },
        { id: 'Members', icon: Users, label: 'Membres' },
        { id: 'Settings', icon: Settings, label: 'Paramètres' },
    ].filter(item => {
        if (currentUser?.role === 'Étudiant' && item.id === 'Settings') return false;
        if (currentUser?.role === 'Enseignant' && item.id === 'Settings') return false;
        return true;
    });

    const handleItemClick = (id) => {
        setActiveItem(id);
        // Close sidebar on mobile after selection
        if (window.innerWidth < 1024) {
            setIsOpen(false);
        }
    };

    return (
        <>
            {/* Mobile Overlay */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsOpen(false)}
                        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    />
                )}
            </AnimatePresence>

            {/* Sidebar */}
            <aside
                className={cn(
                    "h-screen w-72 bg-card/95 backdrop-blur-xl border-r border-border flex flex-col p-6 fixed left-0 top-0 z-50 transition-transform duration-300",
                    // Mobile: slide in/out based on isOpen
                    isOpen ? "translate-x-0" : "-translate-x-full",
                    // Desktop: always visible
                    "lg:translate-x-0"
                )}
            >
                {/* Close button for mobile */}
                <button
                    onClick={() => setIsOpen(false)}
                    className="absolute top-6 right-6 p-2 rounded-lg hover:bg-muted transition-colors lg:hidden"
                >
                    <X size={20} className="text-foreground" />
                </button>

                <div className="flex items-center gap-3 px-2 mb-10">
                    <div className="p-2 bg-primary/20 rounded-lg border border-primary/20">
                        <Library className="text-primary h-6 w-6" />
                    </div>
                    <h1 className="text-xl font-bold bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">
                        Biblius
                    </h1>
                </div>

                <div className="flex-1 space-y-2">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4 px-2">
                        Menu
                    </div>
                    {menuItems.map((item) => (
                        <SidebarItem
                            key={item.id}
                            icon={item.icon}
                            label={item.label}
                            active={activeItem === item.id}
                            onClick={() => handleItemClick(item.id)}
                        />
                    ))}
                </div>

                <div className="mt-auto pt-6 border-t border-border">
                    <SidebarItem icon={LogOut} label="Déconnexion" onClick={onLogout} />
                </div>
            </aside>
        </>
    );
}
